import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import Loader2 from '../../components/loader2';
import showToast from '../../components/ShowToast';
import OnboardingProgressBar from '../../components/Projects/OnboardingProgressBar';
import './SelectProjectTemplate.css';

// 1. Import the specific icons you need from lucide-react
import { 
    ChefHat,         // for kitchen_icon
    Bath,            // for bathroom_icon
    Armchair,        // for deck_icon (implies outdoor living)
    Home,            // for home_icon
    Umbrella,        // for umbrella_icon
    PaintRoller,     // for paint_roller_icon
    Hammer,          // for tool_icon
    Fence,           // for fence_icon (Ensure lucide-react is updated, or use 'Shield')
    CarFront,        // for car_icon
    Waves,           // for waves_icon
    Key,             // for key_icon
    TrendingUp,      // for trending_up_icon
    FileText,        // for file_text_icon
    Construction,    // Fallback icon
    Wrench           // Alternative for tools
} from 'lucide-react';

// 2. Create a mapping object: DB String -> Icon Component
const iconMapping = {
    "kitchen_icon": ChefHat,
    "bathroom_icon": Bath,
    "deck_icon": Armchair,
    "home_icon": Home,
    "umbrella_icon": Umbrella,
    "paint_roller_icon": PaintRoller,
    "tool_icon": Hammer, 
    "fence_icon": Fence, 
    "car_icon": CarFront,
    "waves_icon": Waves,
    "key_icon": Key,
    "trending_up_icon": TrendingUp,
    "file_text_icon": FileText
};

const SelectProjectTemplate = () => {
    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/ProjectTemplates`);
                setTemplates(response.data);
            } catch (err) {
                setError('Failed to fetch project templates.');
                console.error('Error fetching project templates:', err);
                showToast('error', 'Failed to load project templates.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchTemplates();
    }, []);

    const handleTemplateSelect = (templateId) => {
        navigate(`/ai-project-onboarding/location/${templateId}`);
    };

    // 3. Helper function to render the icon safely
    const renderIcon = (iconName) => {
        // Find the icon in the map, or use 'Construction' as a default fallback
        const IconComponent = iconMapping[iconName] || Construction;
        
        // Render it with a class for styling
        return <IconComponent size={48} className="template-icon-svg" />;
    };

    if (isLoading) {
        return <Loader2 />;
    }

    if (error) {
        return <div className="error-message">{error}</div>;
    }

    return (
        <div className="select-template-container">
            <div className="template-header-actions">
                <button className="cancel-btn" onClick={() => navigate('/projects')}>Cancel</button>
            </div>
            <OnboardingProgressBar currentStep={1} />
            <h2>Select a Project Type</h2>
            <p>Choose a pre-defined project template to get started with AI assistance.</p>

            <div className="template-grid">
                {templates.map(template => (
                    <div key={template.templateID} className="template-card" onClick={() => handleTemplateSelect(template.templateID)}>
                        
                        {/* 4. Replaced the <img> tag with our helper function */}
                        <div className="icon-wrapper">
                            {renderIcon(template.iconName)}
                        </div>

                        <h3>{template.name}</h3>
                        <p>{template.description}</p>
                    </div>
                ))}
            </div>

            {templates.length === 0 && !isLoading && (
                <p className="no-templates-message">No project templates available. Please contact support.</p>
            )}
        </div>
    );
};

export default SelectProjectTemplate;